import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import * as PaymentsActions from '../../actions';
import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as PaymentsSelectors from '../../selectors';
import * as PaymentsUtils from '../../utils';

import './UpgradeCard.scss';

export class UpgradeCard extends React.Component {
  static propTypes = {
    upgradeOfferData: PropTypes.shape({
      theme: PropTypes.string,
      offerType: PropTypes.string,
      ctaButtonHref: PropTypes.string,
      infoContentActiveList: PropTypes.array,
      infoContentInactiveList: PropTypes.array,
    }).isRequired,
    tier: PropTypes.string,
    offerTrialType: PropTypes.string,
    discountType: PropTypes.string,
    price: PropTypes.string,
    backingProductPrice: PropTypes.string,
    durationType: PropTypes.string,
    trialDurationTime: PropTypes.string,
    trialDurationType: PropTypes.string,
    trialTimesRenewable: PropTypes.number,
    isOfferedPaidTrial: PropTypes.bool,
    isOfferedFreeTrial: PropTypes.bool,
    isMobileDevice: PropTypes.bool,
    isOnPayments: PropTypes.bool,
    openModal: PropTypes.func,
    anonymousStartTrialClicked: PropTypes.func,
    upgradeCtaClicked: PropTypes.func,
    upgradeDiscountPlanLinkClicked: PropTypes.func,
    isAnon: PropTypes.bool,
    isOldAndroid: PropTypes.bool,
    isPremiumTier: PropTypes.bool,
    isPremiumFamilyTier: PropTypes.bool,
    hasDiscountType: PropTypes.bool,
    specialOfferCode: PropTypes.string,
    linkClicked: PropTypes.func,
    locale: PropTypes.string,
    isSingleCard: PropTypes.bool,
    isLanding: PropTypes.bool,
    isGiftee: PropTypes.bool,
    isListenerIneligible: PropTypes.bool,
  };

  premiumFeaturesOpenModalOnclick = () => {
    const { linkClicked, openModal } = this.props;
    linkClicked(D.Constants.Onboarding.StatsOnboardingActions.PREMIUM_FEATURES);
    openModal({
      modalType: D.Constants.Modal.ModalTypes.STANDARD,
      body: this.getPremiumFeaturesList(),
    });
  };

  getSecondaryText = () => {
    const {
      isOfferedPaidTrial,
      isGiftee,
      locale,
      tier,
      discountType,
      offerTrialType,
      backingProductPrice,
      isListenerIneligible,
    } = this.props;
    let { durationType, price } = this.props;
    if (offerTrialType === PaymentsConstants.OfferTrialTypes.ONCE_BILLED || isListenerIneligible) {
      price = backingProductPrice;
    }
    if (isGiftee) {
      // Giftees are only returned info for their same tier in getAvailableProducts
      // We have to use defaults if we need other tier info to display
      durationType = PaymentsConstants.DurationTypes.MONTHLY;
      price = PaymentsUtils.getDefaultPrice({
        tier,
        discountType,
        durationType,
      });
    }
    if (isOfferedPaidTrial && durationType === PaymentsConstants.DurationTypes.YEARLY) {
      return;
    }

    let planPriceOverDuration;
    if (price) {
      const formattedPrice = D.Utils.StringUtils.returnPriceString(price, locale);
      planPriceOverDuration = (
        <FormattedMessage
          {...PaymentsConstants.Messages.pandoraPlanWithPrice}
          values={{
            price: formattedPrice,
            frequency: PaymentsUtils.getPaymentFrequencyForDurationType(durationType),
          }}
        />
      );
    } else {
      planPriceOverDuration = <FormattedMessage {...PaymentsConstants.Messages.free} />;
    }

    return <div data-qa={D.Constants.QATags.UCST}>{planPriceOverDuration}</div>;
  };

  getPremiumFeaturesList = () => {
    const featureListProps = {
      primaryText: PaymentsUtils.getPlanName(
        PaymentsConstants.tiers.PANDORA_PREMIUM,
        PaymentsConstants.discountTypes.NONE,
      ),
      infoContent: this.getUpgradeCardContent(PaymentsConstants.Upgrade.PandoraPremiumFeaturesList),
    };

    return (
      <div className='UpgradeCardModal'>
        <D.InfoCard {...featureListProps} />
      </div>
    );
  };

  getFooterLinkData = ({ tier, discountType, href, message }) => {
    const { isOfferedFreeTrial } = this.props;

    const onClick = () => {
      this.props.upgradeDiscountPlanLinkClicked({
        tier,
        discountType,
        isTrial: isOfferedFreeTrial,
      });
      this.props.linkClicked(PaymentsUtils.getPlanWebRegistration(tier, discountType));
    };

    return {
      label: <FormattedMessage {...message} />,
      href,
      qaTag: `${D.Constants.QATags.UCPLL}_${message.defaultMessage}`,
      onClick,
    };
  };

  getPremiumFooterList = () => {
    return [
      this.getFooterLinkData({
        tier: PaymentsConstants.tiers.PANDORA_FAMILY,
        discountType: PaymentsConstants.discountTypes.NONE,
        message: PaymentsConstants.Upgrade.pipedLinksMessages.family,
        href: D.Utils.Route.getFamilyPlanUpgradePath(),
      }),
      this.getFooterLinkData({
        tier: PaymentsConstants.tiers.PANDORA_PREMIUM,
        discountType: PaymentsConstants.discountTypes.STUDENT,
        message: PaymentsConstants.Upgrade.pipedLinksMessages.student,
        href: D.Utils.Route.getPremiumStudentPlanUpgradePath(),
      }),
      this.getFooterLinkData({
        tier: PaymentsConstants.tiers.PANDORA_PREMIUM,
        discountType: PaymentsConstants.discountTypes.MILITARY,
        message: PaymentsConstants.Upgrade.pipedLinksMessages.military,
        href: D.Utils.Route.getPremiumMilitaryPlanUpgradePath(),
      }),
    ];
  };

  getPremiumPipedLinks = () => {
    const title = (
      <div data-qa={D.Constants.QATags.UCPLT}>
        <FormattedMessage
          {...PaymentsConstants.Upgrade.UpgradePlanMessages.discountedPremiumPlans}
        />
      </div>
    );

    return <D.PipedLinks title={title} links={this.getPremiumFooterList()} />;
  };

  getCTAButtonText = () => {
    const {
      locale,
      price,
      tier,
      isOfferedFreeTrial,
      trialDurationTime,
      trialDurationType,
      trialTimesRenewable,
      offerTrialType,
      isOnPayments,
      isListenerIneligible,
    } = this.props;

    if (isListenerIneligible) {
      return;
    }
    switch (offerTrialType) {
      case PaymentsConstants.OfferTrialTypes.SIX_MONTH_ANNUAL_PREMIUM: {
        if (isOnPayments) {
          return;
        }
        return <FormattedMessage {...PaymentsConstants.Upgrade.UpgradeCTAMessages.upgradeNow} />;
      }
      case PaymentsConstants.OfferTrialTypes.FREE: {
        const trialDisplayableDuration = PaymentsUtils.formatDisplayableDuration(
          trialDurationTime,
          trialDurationType,
        );
        const msg = isOnPayments
          ? PaymentsConstants.Upgrade.UpgradeCTAMessages.durationForFree
          : PaymentsConstants.Upgrade.UpgradeCTAMessages.getDurationForFree;
        return <FormattedMessage {...msg} values={{ trialDisplayableDuration }} />;
      }
      case PaymentsConstants.OfferTrialTypes.ONCE_BILLED: {
        const displayablePrice = D.Utils.StringUtils.returnPriceString(price, locale);
        const trialDisplayableDuration = PaymentsUtils.formatDisplayableDuration(
          trialDurationTime,
          trialDurationType,
        );
        return (
          <FormattedMessage
            {...PaymentsConstants.Upgrade.UpgradeCTAMessages.onceBilled}
            values={{ trialDisplayableDuration, displayablePrice }}
          />
        );
      }
      case PaymentsConstants.OfferTrialTypes.MONTHLY_BILLED_LIMITED: {
        const displayablePrice = D.Utils.StringUtils.returnPriceString(price, locale);
        const frequency = PaymentsUtils.getPaymentFrequencyForDurationType(trialDurationType);
        const trialDisplayableDuration = PaymentsUtils.formatDisplayableDuration(
          trialTimesRenewable + 1,
          trialDurationType,
        );
        return (
          <FormattedMessage
            {...PaymentsConstants.Upgrade.UpgradeCTAMessages.monthlyBilledLimited}
            values={{ trialDisplayableDuration, displayablePrice, frequency }}
          />
        );
      }
      case PaymentsConstants.OfferTrialTypes.NO_TRIAL:
        if (tier === PaymentsConstants.tiers.AD_SUPPORTED) {
          return (
            <FormattedMessage {...PaymentsConstants.Upgrade.UpgradeCTAMessages.signUpForFree} />
          );
        } else if (!isOnPayments) {
          return <FormattedMessage {...PaymentsConstants.Upgrade.UpgradeCTAMessages.upgradeNow} />;
        }
        return;
    }

    if (PaymentsUtils.isAdSupportedTier(tier)) {
      return <FormattedMessage {...PaymentsConstants.Upgrade.UpgradeCTAMessages.signUpForFree} />;
    } else if (isOfferedFreeTrial) {
      const trialDisplayableDuration = PaymentsUtils.formatDisplayableDuration(
        trialDurationTime,
        trialDurationType,
      );
      return (
        <FormattedMessage
          {...PaymentsConstants.Upgrade.UpgradeCTAMessages.getDurationForFree}
          values={{ trialDisplayableDuration }}
        />
      );
    } else if (!isOnPayments) {
      return <FormattedMessage {...PaymentsConstants.Upgrade.UpgradeCTAMessages.upgradeNow} />;
    }
  };

  getCTAButton = () => {
    const {
      upgradeOfferData: { theme, ctaButtonHref },
      isAnon,
      upgradeCtaClicked,
      tier,
      discountType,
      isOfferedFreeTrial,
      anonymousStartTrialClicked,
      isOnPayments,
    } = this.props;

    const buttonText = this.getCTAButtonText();
    if (!buttonText) {
      return;
    }

    let href = ctaButtonHref;
    let onClick;

    if (isAnon) {
      const redirectAfterLogin = href;
      href = null;
      onClick = () => {
        upgradeCtaClicked({ tier, discountType, isTrial: isOfferedFreeTrial });
        anonymousStartTrialClicked({
          tier,
          discountType,
          shouldSetAnon: true,
          redirectAfterLogin,
        });
      };
    } else {
      onClick = () => {
        upgradeCtaClicked({ tier, discountType, isTrial: isOfferedFreeTrial });
      };
    }

    return (
      <D.ButtonT3
        theme={theme}
        isInverted={isOnPayments}
        isDisabled={isOnPayments}
        size={D.ButtonT3Size.LARGE}
        isPillShaped={true}
        isFull={true}
        href={!isOnPayments && href}
        onClick={onClick}
        qaTag={D.Constants.QATags.UCC}
      >
        {buttonText}
      </D.ButtonT3>
    );
  };

  getInfoContentCTA = () => {
    const { isPremiumTier, hasDiscountType, isMobileDevice } = this.props;
    if (!isPremiumTier || hasDiscountType || !isMobileDevice) {
      return;
    }

    return this.getPremiumPipedLinks();
  };

  getInfoContentActiveList = () => {
    const {
      upgradeOfferData: { infoContentActiveList },
      isPremiumFamilyTier,
    } = this.props;
    if (isPremiumFamilyTier) {
      const premiumFeaturesLink = (
        <Link onClick={this.premiumFeaturesOpenModalOnclick} data-qa={D.Constants.QATags.UCCLPFML}>
          <FormattedMessage {...PaymentsConstants.Upgrade.FamilyPlanMessages.premiumFeatures} />
        </Link>
      );
      return [
        ...infoContentActiveList,
        <FormattedMessage
          key={0}
          {...PaymentsConstants.Upgrade.FamilyPlanMessages.allPandoraPremiumFeatures}
          values={{ premiumFeaturesLink }}
        />,
      ];
    }
    return infoContentActiveList;
  };

  getUpgradeCardList = (listArray, modifier) => {
    const listItemClasses = D.createBEM('UpgradeCard__listItem', {
      [modifier]: !!modifier,
    });
    return (
      <ul className='UpgradeCard__list' data-qa={`${D.Constants.QATags.UCCL}_${modifier}`}>
        {listArray.map((listItem, index) => {
          return (
            <li className={listItemClasses} key={index}>
              {listItem}
            </li>
          );
        })}
      </ul>
    );
  };

  getUpgradeCardContent = (infoContentActiveList, infoContentInactiveList) => {
    return (
      <React.Fragment>
        {infoContentActiveList && this.getUpgradeCardList(infoContentActiveList, 'active')}
        {infoContentInactiveList && this.getUpgradeCardList(infoContentInactiveList, 'inactive')}
      </React.Fragment>
    );
  };

  getUnlockWithAdMessage = () => {
    return (
      <div data-qa={D.Constants.QATags.UCFT}>
        <FormattedMessage {...PaymentsConstants.Upgrade.UpgradePlanMessages.unlockByViewingAnAd} />
      </div>
    );
  };

  getInfoContentFooter = () => {
    const {
      isPremiumFamilyTier,
      isMobileDevice,
      isPremiumTier,
      hasDiscountType,
      specialOfferCode,
      isOnPayments,
    } = this.props;
    if (
      isPremiumFamilyTier ||
      (isMobileDevice && isPremiumTier) ||
      hasDiscountType ||
      (!!specialOfferCode && isOnPayments)
    ) {
      return;
    }

    return isPremiumTier ? this.getPremiumPipedLinks() : this.getUnlockWithAdMessage();
  };

  render() {
    const {
      upgradeOfferData: { theme, infoContentInactiveList },
      tier,
      hasDiscountType,
      discountType,
      isPremiumTier,
      isMobileDevice,
      isOldAndroid,
      linkClicked,
      isSingleCard,
      isLanding,
    } = this.props;
    const tierModifier = tier && tier.toLowerCase();
    const discountTypeModifier = discountType && discountType.toLowerCase();
    const defaultQATag = `${D.Constants.QATags.UC}_${tierModifier}`;
    const qaTag = hasDiscountType ? `${defaultQATag}_${discountTypeModifier}` : defaultQATag;
    const planName = PaymentsUtils.getPlanName(tier, discountType);
    const eventAction = planName.split(' ')[1] && planName.split(' ')[1].toLowerCase();

    return (
      <D.InfoCard
        theme={theme}
        primaryText={
          <div data-qa={D.Constants.QATags.UCPT}>
            {PaymentsUtils.getPlanName(tier, discountType)}
          </div>
        }
        secondaryText={this.getSecondaryText()}
        ctaButton={this.getCTAButton()}
        infoContentCTA={this.getInfoContentCTA()}
        infoContent={this.getUpgradeCardContent(
          this.getInfoContentActiveList(),
          infoContentInactiveList,
        )}
        withFooterBar={!isPremiumTier}
        infoContentFooter={this.getInfoContentFooter()}
        isMobile={isMobileDevice}
        isOldAndroid={isOldAndroid}
        logEvent={linkClicked}
        eventAction={eventAction}
        qaTag={qaTag}
        isSingleCard={isSingleCard}
        isLanding={isLanding}
      />
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const specialOfferTier = PaymentsSelectors.getSpecialOfferTier(state);
  const specialOfferDiscountType = PaymentsSelectors.getSpecialOfferDiscountType(state);

  const isInSpecialOfferFlow = PaymentsSelectors.isInSpecialOfferFlow(state);
  const tier = specialOfferTier ? specialOfferTier : ownProps.tier;
  const discountType = isInSpecialOfferFlow ? specialOfferDiscountType : ownProps.discountType;

  const durationType = PaymentsSelectors.createGetUpgradeOfferDurationTypeSelector(state)(
    tier,
    discountType,
  );
  const price = PaymentsSelectors.createGetUpgradeOfferPriceSelector(state)(
    tier,
    discountType,
    durationType,
  );
  const backingProductPrice = PaymentsSelectors.createGetBackingProductPriceSelector(state)(
    tier,
    discountType,
    durationType,
  );

  return {
    tier,
    discountType,
    price,
    backingProductPrice,
    durationType,
    upgradeOfferData: PaymentsUtils.getUpgradeCardData(tier, discountType),
    offerDurationType: PaymentsSelectors.createGetOfferDurationTypeSelector(state)(
      tier,
      discountType,
    ),
    isOfferedPaidTrial: PaymentsSelectors.createIsOfferedPaidTrialSelector(state)(
      tier,
      discountType,
    ),
    isOfferedFreeTrial: PaymentsSelectors.createIsOfferedFreeTrialSelector(state)(
      tier,
      discountType,
    ),
    trialDurationTime: PaymentsSelectors.createGetTrialDurationTimeSelector(state)(
      tier,
      discountType,
    ),
    trialTimesRenewable: PaymentsSelectors.createGetTrialTimesRenewableSelector(state)(
      tier,
      discountType,
    ),
    trialDurationType: PaymentsSelectors.createGetTrialDurationTypeSelector(state)(
      tier,
      discountType,
    ),
    offerTrialType: PaymentsSelectors.createGetOfferTrialTypeSelector(state)(tier, discountType),
    isGiftee: PaymentsSelectors.getIsGiftee(state),
    isPremiumTier: PaymentsUtils.isPremiumTier(tier),
    isPremiumFamilyTier: PaymentsUtils.isPremiumFamilyTier(tier),
    hasDiscountType: PaymentsUtils.isDiscount(discountType),
    specialOfferCode: PaymentsSelectors.getSpecialOfferCode(state),
    isMobileDevice: D.isMobileDevice(state),
    isAnon: D.Utils.Listener.isAnonymousListener(state),
    locale: D.Utils.i18n.getLocale(state),
  };
}

const mapDispatchToProps = {
  anonymousStartTrialClicked: PaymentsActions.anonymousStartTrialClicked,
  upgradeCtaClicked: PaymentsActions.upgradeCtaClicked,
  upgradeDiscountPlanLinkClicked: PaymentsActions.upgradeDiscountPlanLinkClicked,
  openModal: D.Actions.Modal.openModal,
  linkClicked: D.Actions.UseCases.linkClicked,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeCard);
